import type { zhCN } from './zh-CN.ts';

/**
 * English dictionary — mirrors `zh-CN.ts`'s key space exactly via the
 * `Record<keyof typeof zhCN, string>` annotation. Removing or misnaming
 * a key here is a type error. Adding a new key: update `zh-CN.ts` first;
 * TS will then require the same key here.
 *
 * Value type is widened to `string` (not the literal-string type
 * `typeof zhCN` produces under `as const`) so each locale can carry its
 * own copy without the compiler demanding the Chinese phrase verbatim.
 * The key-space guarantee is the contract we need; the value is free-
 * form by design.
 *
 * Copy style
 * - Sentence-case for user-facing text (not Title Case), matching the
 *   existing console-web conventions in the pages before this PR.
 * - Use en-dash (`–`) for numeric ranges (e.g. `10–128 chars`) — same
 *   punctuation the pre-i18n sources used.
 * - Prefer imperative verbs in buttons ("Sign in", not "Please sign in").
 */
export const en: Record<keyof typeof zhCN, string> = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nadoutong Console',
  'meta.description':
    'Nadoutong developer console: manage API keys, review usage, top up balance, and invite teammates.',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': 'Nadoutong Console',
  'common.session.checking': 'Checking session…',
  'common.back_to_login': 'Back to sign-in',
  'common.technical_details.title': 'Technical details',
  'common.technical_details.show': 'Show',
  'common.technical_details.hide': 'Hide',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — see the canonical zh-CN dictionary for
  // the explanatory note.

  // ── Login ───────────────────────────────────────────────────────
  'login.subtitle': 'Sign in to your Nadoutong account',
  'login.description': 'Manage API keys, balance, usage, and teammates after you sign in.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.need_account': 'Need an account? Register',
  'login.have_invite_code': 'Have an invite code? Activate',
  'login.forgot': 'Forgot password?',
  'login.error.empty_fields': 'Email and password are required',

  // ── Login · email sign-in code (epic #1475) ─────────────────────
  // The conditional "If … has an account" phrasing is deliberate: the
  // request endpoint answers identically for registered and unknown
  // addresses, and the copy must not give away what the API withholds.
  'login.mode.label': 'Sign-in method',
  'login.mode.password': 'Password',
  'login.mode.code': 'Email code',
  'login.code.send': 'Send code',
  'login.code.sending': 'Sending…',
  'login.code.label': '6-digit code',
  'login.code.ack':
    'If {email} has an account, a code is on its way. It expires in {minutes} minutes.',
  'login.code.ack_resent':
    'Sent again. If {email} has an account, the new code expires in {minutes} minutes.',
  'login.code.resend': 'Resend',
  'login.code.change_email': 'Use another email',
  'login.code.error.empty_email': 'Email is required',
  'login.code.error.format': 'The code is 6 digits',
  'login.code.error.invalid': 'That code is not right — check it and try again',
  'login.code.error.expired': 'That code has expired — request a new one',
  'login.code.error.attempts_exhausted': 'Too many attempts — request a new code',
  'login.code.error.send_rate_limited': 'Too many requests — try again in a moment',
  'login.code.error.verify_rate_limited': 'Too many attempts — try again in a moment',
  'login.code.error.account_suspended': 'This account is suspended. Contact your account manager.',
  'login.code.error.account_deleted': 'This account has been closed',

  // ── Activate (invite-code activation, epic #1475) ───────────────
  'activate.subtitle': 'Activate with an invite code',
  'activate.description':
    'Enter your email and the invite code from your account manager to open an account and claim your trial credit.',
  'activate.email': 'Email',
  'activate.invite_code': 'Invite code',
  'activate.submit': 'Activate account',
  'activate.submitting': 'Activating…',
  'activate.have_account': 'Already have an account? Sign in',
  'activate.no_code': 'No invite code? Register',
  'activate.error.empty_fields': 'Email and invite code are required',
  'activate.error.code_invalid':
    'That invite code is not valid or is no longer active — check with your account manager',
  'activate.error.quota_exhausted':
    'This invite code is out of slots for today — try tomorrow or ask your account manager',
  'activate.error.email_taken': 'That email already has an account',
  'activate.error.email_taken_cta': 'Sign in instead',
  'activate.error.rate_limited': 'Too many attempts — try again in a moment',
  'activate.success.title': 'Your account is ready',
  'activate.success.description':
    'Your trial credit is in the account — you can start calling the API now.',
  'activate.success.grant_title': 'Activated',
  'activate.success.grant_body': '{amount} trial credit added, valid until {expiresAt}.',
  'activate.success.passwordless_hint':
    'This account has no password — sign in with an email code next time.',
  'activate.success.cta': 'Go to console',

  // ── Register ────────────────────────────────────────────────────
  'register.subtitle': 'Create your Nadoutong account',
  'register.description': 'Start with your own account, then invite teammates when you are ready.',
  'register.email': 'Email',
  'register.password_with_hint': 'Password (10–128 chars)',
  'register.workspace_name': 'Team name (optional)',
  'register.workspace_placeholder': 'For example: Payments / Data Platform / My Team',
  'register.submit': 'Create account',
  'register.submitting': 'Creating account…',
  'register.error.empty_fields': 'Email and password are required',
  'register.error.password_too_short': 'Password must be at least 10 characters',
  'register.success.title': 'Check your inbox',
  'register.success.body':
    'We sent a verification link to {email}. Open the link in the email to continue into the console.',
  'register.success.resend_ack':
    'A new verification link is on its way. If it still does not arrive, check spam or wait a minute before trying again.',
  'register.success.resend_hint': 'If the email has not arrived yet, you can send another one.',
  'register.resend.submit': 'Resend link',
  'register.resend.submitting': 'Sending…',
  'register.already_have_account': 'Already have an account? Sign in',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.subtitle': 'Reset your sign-in password',
  'forgot.description': 'Enter your account email and we will send you a reset link.',
  'forgot.email': 'Email',
  'forgot.submit': 'Send reset link',
  'forgot.submitting': 'Sending…',
  'forgot.error.empty_email': 'Email is required',
  'forgot.success.title': 'Check your email',
  'forgot.success.body':
    'If an account exists for {email}, we sent a reset link. The link is valid for 24 hours.',

  // ── Reset password ──────────────────────────────────────────────
  'reset.subtitle': 'Set a new password',
  'reset.description': 'Save the new password, then head back to sign in.',
  'reset.new_password': 'New password (10–128 chars)',
  'reset.confirm_password': 'Confirm new password',
  'reset.submit': 'Save password',
  'reset.submitting': 'Saving…',
  'reset.error.missing_token': 'Reset token missing from link',
  'reset.error.password_too_short': 'Password must be at least 10 characters',
  'reset.error.password_mismatch': 'Passwords do not match',
  'reset.error.token_banner':
    'Reset token missing — open the email link directly, or request a new one.',
  'reset.success.title': 'Password reset',
  'reset.success.body': 'Your password has been updated. Sign in with the new password.',
  'reset.success.sign_in': 'Sign in',

  // ── Verify email ────────────────────────────────────────────────
  'verify.subtitle': 'Finish verifying your email',
  'verify.description': 'Once verification finishes, use the button below to enter the console.',
  'verify.missing_token':
    'This link is missing the verification token. Open the email link directly, or request a fresh one from the sign-in page.',
  'verify.pending': 'Verifying your email…',
  'verify.success.title': 'Email verified',
  'verify.success.body': 'Your email address is verified and your account is ready to use.',
  'verify.success.cta': 'Enter the console',
  'verify.error.title': 'Verification failed',
  'verify.resend.label': 'Request a new link',
  'verify.resend.submit': 'Send a new link',
  'verify.resend.submitting': 'Sending…',
  'verify.resend.ack.title': 'Check your inbox',
  'verify.resend.ack.body':
    'If an account exists for that address and has not yet been verified, a fresh link is on its way.',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.subtitle': 'Join a team',
  'invite.description': 'Review the team details first, then finish joining.',
  'invite.loading': 'Loading invitation…',
  'invite.error.missing_token': 'Missing invitation token in the URL',
  'invite.error.title': 'Invitation unavailable',
  'invite.invite_to': "You're invited to",
  'invite.role': 'Role',
  'invite.invitee_email': 'Invitee email',
  'invite.invited_by': 'Invited by',
  'invite.expires': 'Expires',
  'invite.password_with_hint': 'Password (10–128 chars)',
  'invite.display_name': 'Display name (optional)',
  'invite.display_name_placeholder': 'Defaults to email prefix',
  'invite.session_match': "You're signed in as {email}. Click accept to join {org}.",
  'invite.session_mismatch.body':
    "You're currently signed in as {current}, but this invitation is for {target}. Enter the password for that account to accept, or ",
  'invite.session_mismatch.switch_link': 'switch account',
  'invite.session_mismatch.suffix': '.',
  'invite.accept': 'Accept invitation',
  'invite.join': 'Join team',
  'invite.accepting': 'Accepting…',
  'invite.cancel': 'Cancel',
  'invite.error.password_too_short': 'Password must be at least {min} characters',
  'invite.error.password_too_long': 'Password must be at most {max} characters',
  'invite.toast.accepted': 'Invitation accepted',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': 'Page not found',
  'notfound.body': 'The page you were looking for does not exist in this console.',
  'notfound.cta': 'Go to dashboard',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.title': 'Nadoutong',
  'shell.brand.subtitle': 'API Console',
  'shell.brand.supporting': 'Manage your API keys, balance, usage, and teammates in one place.',
  'shell.nav.aria_label': 'Primary',
  'shell.nav.open': 'Open navigation',
  'shell.nav.close': 'Close navigation',
  'shell.sidebar.collapse': 'Collapse sidebar',
  'shell.sidebar.expand': 'Expand sidebar',
  'shell.cmdk.trigger': 'Search pages / requests…',
  'shell.cmdk.placeholder': 'Type a page name or req_ request id',
  'shell.cmdk.empty': 'No matches',
  'shell.cmdk.aria_label': 'Command palette',
  'shell.cmdk.footer_hint': '↑↓ select · Enter open · Esc close',
  'shell.cmdk.group.requests': 'Requests',
  'shell.cmdk.group.recent': 'Recent',
  'shell.cmdk.open_request': 'View request {id}',
  'shell.nav.group.overview': 'Overview',
  'shell.nav.dashboard': 'Overview',
  'shell.nav.group.access': 'Access',
  'shell.nav.keys': 'Keys',
  'shell.nav.access_docs': 'Access docs',
  'shell.nav.service_status': 'Service status',
  'shell.nav.group.usage_diagnostics': 'Usage & diagnostics',
  'shell.nav.usage': 'Usage',
  'shell.nav.group.team': 'Team',
  'shell.nav.requests': 'Request logs',
  'shell.nav.group.cost': 'Cost',
  'shell.nav.pricing': 'Model pricing',
  'shell.nav.billing': 'Balance & billing',
  'shell.nav.topup': 'Top up',
  'shell.nav.team_members': 'Members',
  'shell.nav.team_model_permissions': 'Model permissions',
  'shell.nav.team_org_units': 'Departments',
  'shell.nav.team_audit': 'Audit',
  'shell.nav.group.settings': 'Settings',
  'shell.nav.settings': 'Settings',
  'shell.nav.settings_account': 'Account',
  'shell.nav.settings_security': 'Security',
  'shell.nav.settings_technical': 'Technical info',
  'shell.org_chip.title': 'Current team',
  'shell.page.console_fallback': 'Console',
  'shell.session.active': 'Current session',
  'shell.session.verified': 'Email verified',
  'shell.session.unverified': 'Email verification pending',
  'shell.session.signed_in': 'signed in',
  'shell.logout': 'Log out',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': 'Overview',
  'dashboard.header.sub': 'Workspace account {email}',
  'dashboard.retry': 'Retry',
  'dashboard.hero.caption': 'Account balance',
  'dashboard.hero.funded': 'funded',
  'dashboard.hero.zero': 'zero balance',
  'dashboard.hero.updated': 'Updated {timestamp}',
  'dashboard.hero.topup': 'Top up',
  'dashboard.hero.zero_readonly': 'Balance is empty. Ask an owner or admin to top up.',
  'dashboard.hero.error': 'Could not load balance.',
  'dashboard.kpi.month_spend.label': 'Spend this month',
  'dashboard.kpi.month_spend.hint': '{tokens} tokens used',
  'dashboard.kpi.active_keys.label': 'Active keys',
  'dashboard.kpi.active_keys.hint': 'Manage keys',
  'dashboard.kpi.last_request.label': 'Last request',
  'dashboard.kpi.last_request.value': '~{time}',
  'dashboard.kpi.last_request.hint': 'Approximate — from hourly rollups',
  'dashboard.kpi.last_request.empty': 'No requests yet',
  'dashboard.kpi.unavailable': 'Failed to load',
  'dashboard.status.caption': 'Service status',
  'dashboard.status.overall.healthy': 'All services operational',
  'dashboard.status.overall.busy': 'Some services busy',
  'dashboard.status.overall.unavailable': 'Some services unavailable',
  'dashboard.status.updated': 'Updated {timestamp}',
  'dashboard.status.view': 'View service status',
  'dashboard.status.error': 'Could not load service status.',
  'dashboard.quick.caption': 'Quick actions',
  'dashboard.quick.create_key': 'Create key',
  'dashboard.quick.topup': 'Top up',
  'dashboard.quick.requests': 'Request diagnostics',
  'dashboard.quick.pricing': 'Model pricing',
  'dashboard.trend.requests.caption': 'Requests — last 7 days',
  'dashboard.trend.spend.caption': 'Spend — last 7 days',
  'dashboard.trend.view_usage': 'View usage analytics',
  'dashboard.trend.error': 'Could not load trend data.',
  'dashboard.trend.empty': 'No data yet',
  'dashboard.activity.title': 'Recent activity',
  'dashboard.activity.view_all': 'View all usage',
  'dashboard.activity.error': 'Could not load recent activity.',
  'dashboard.activity.empty': 'No requests in the last 7 days yet.',
  'dashboard.activity.empty_hint': 'Create a key, then send your first request.',
  'dashboard.activity.column.time': 'Time',
  'dashboard.activity.column.requests': 'Requests',
  'dashboard.activity.column.tokens': 'Tokens',
  'dashboard.activity.column.cost': 'Cost',
  'dashboard.top_keys.title': 'Most-used keys this month',
  'dashboard.top_keys.view_usage': 'View in usage',
  'dashboard.top_keys.error': 'Could not load key usage.',
  'dashboard.top_keys.empty': 'No key usage yet.',
  'dashboard.top_keys.empty_hint': 'Keys will show up here once they start handling requests.',
  'dashboard.top_keys.column.key': 'Key',
  'dashboard.top_keys.column.requests': 'Requests',
  'dashboard.top_keys.column.cost': 'Cost',

  // ── API keys page (/access/keys) ───────────────────────────────
  'keys.title': 'Keys',
  'keys.subtitle': 'Create and manage the keys your applications call Nadoutong with.',
  'keys.create': 'Create key',
  'keys.retry': 'Retry',
  'keys.empty.title': 'No keys yet',
  'keys.empty.hint':
    'A key is the credential your application uses to call the Nadoutong API. Create one, drop it into your tool, and start making requests.',
  'keys.column.prefix': 'Prefix',
  'keys.column.name': 'Name',
  'keys.column.spend_limits': 'Spend limits',
  'keys.column.created': 'Created',
  'keys.column.last_used': 'Last used',
  'keys.column.actions': 'Actions',
  'keys.never_used': 'Never',
  'keys.edit': 'Edit',
  'keys.revoke': 'Revoke',
  'keys.limit.none': 'No limit',
  'keys.limit.per_request.label': 'Per request',
  'keys.limit.daily.label': 'Daily',
  'keys.limit.weekly.label': 'Weekly',
  'keys.limit.monthly.label': 'Monthly',
  'keys.limit.per_request.placeholder': '¥ / request',
  'keys.limit.daily.placeholder': '¥ / day',
  'keys.limit.weekly.placeholder': '¥ / week',
  'keys.limit.monthly.placeholder': '¥ / month',
  'keys.drawer.create_title': 'Create key',
  'keys.drawer.create_description':
    'The full key is shown only once — save it right away. Model mappings can be adjusted any time after creation.',
  'keys.drawer.edit_title': 'Edit key',
  'keys.drawer.edit_description':
    'Change the name or spend limits. Only changed fields are submitted.',
  'keys.drawer.close': 'Close',
  'keys.form.name': 'Name',
  'keys.form.name_hint':
    'Optional. Name keys after the tool that uses them to keep usage traceable.',
  'keys.form.name_placeholder': 'production-backend',
  'keys.form.name_required': 'Name cannot be empty',
  'keys.form.template_label': 'Start from a namespace (optional)',
  'keys.form.template_none': 'No preset mappings',
  'keys.form.template_hint':
    'After creation, the server atomically maps the current {count} models in this namespace (alias = model name).',
  'keys.template.failure':
    'The key was created, but its namespace mappings were not applied: {error}',
  'keys.form.limits_label': 'Spend limits (CNY, optional)',
  'keys.form.limits_hint':
    'Leave a field empty for no limit. Limits are enforced from metered usage, so a request in flight may carry usage slightly past the limit before the key is paused.',
  'keys.form.limits_loading': 'Loading exchange rate…',
  'keys.form.limits_rate_unavailable': 'Exchange rate is unavailable — limit editing is disabled.',
  'keys.form.limit_invalid': 'Spend limits must be non-negative CNY amounts.',
  'keys.form.network_error': 'Network error — please try again',
  'keys.form.cancel': 'Cancel',
  'keys.form.submit_create': 'Create key',
  'keys.form.submit_save': 'Save changes',
  'keys.toast.created': 'Key created',
  'keys.toast.saved': 'Changes saved',
  'keys.toast.revoked': 'Key revoked',
  'keys.plaintext.title': 'Copy your API key',
  'keys.plaintext.warning':
    'This is the only time the full key is shown. Copy it now and store it safely; if it is lost, revoke the key and create a new one.',
  'keys.plaintext.name_hint': 'Key name: {name}',
  'keys.plaintext.copied': 'Copied to clipboard',
  'keys.plaintext.copy_anywhere': 'You can also select the key text and copy it directly',
  'keys.plaintext.copy_button': 'Copy key',
  'keys.plaintext.copied_button': 'Copied',
  'keys.plaintext.close': 'I saved it — close',
  'keys.revoke.title': 'Revoke this key?',
  'keys.revoke.description.before': 'After revoking, key ',
  'keys.revoke.description.after':
    ' stops working immediately; applications still using it will start receiving 401 errors. This cannot be undone.',
  'keys.revoke.type_label': 'Type the key name to confirm revocation',
  'keys.revoke.submit': 'Revoke key',
  'keys.revoke.cancel': 'Cancel',
  'keys.revoke.close': 'Close',
  'keys.mappings.button': 'Mappings',
  'keys.mappings.title': 'Model mappings',
  'keys.mappings.description':
    'Map the model names your client sends to catalog listings. Key: {key}',
  'keys.mappings.empty':
    'No mappings yet. Add one so clients can call the target model by that alias.',
  'keys.mappings.column.alias': 'Alias',
  'keys.mappings.column.target': 'Target model',
  'keys.mappings.column.source': 'Source',
  'keys.mappings.source.manual': 'Manual',
  'keys.mappings.source.automatic': 'Automatic',
  'keys.mappings.automatic_managed': 'Managed by namespace',
  'keys.mappings.readonly':
    'You can inspect mappings and conflicts, but your role cannot change them.',
  'keys.mappings.namespace.title': 'Namespace actions',
  'keys.mappings.namespace.description':
    'Choose one namespace. Apply maps its current models once; automatic mapping also includes models added later. Both remove the namespace/ prefix from aliases.',
  'keys.mappings.namespace.select_label': 'Namespace',
  'keys.mappings.namespace.select_placeholder': 'Choose a namespace',
  'keys.mappings.namespace.selected_hint':
    '{count} models are currently visible in this namespace.',
  'keys.mappings.namespace.apply': 'Map current models',
  'keys.mappings.namespace.auto_enable': 'Enable automatic mapping',
  'keys.mappings.namespace.auto_enabled_button': 'Automatic mapping enabled',
  'keys.mappings.namespace.operations_disabled':
    'Mapping current models and enabling new automatic namespaces are temporarily unavailable while the mapping upgrade finishes. Manual mappings and disabling existing automatic namespaces remain available.',
  'keys.mappings.namespace.enabled_title': 'Automatic namespaces',
  'keys.mappings.namespace.enabled_empty': 'No namespaces use automatic mapping.',
  'keys.mappings.namespace.auto_disable': 'Disable',
  'keys.mappings.namespace.applied': 'Mapped {count} current models from {namespace}.',
  'keys.mappings.namespace.auto_enabled': 'Automatic mapping enabled for {namespace}.',
  'keys.mappings.namespace.auto_disabled': 'Automatic mapping disabled for {namespace}.',
  'keys.mappings.namespace.disable_title': 'Disable automatic mapping?',
  'keys.mappings.namespace.disable_description':
    'Automatic mapping from {namespace} will stop. Aliases that also have a manual mapping remain available.',
  'keys.mappings.namespace.disable_confirm': 'Disable automatic mapping',
  'keys.mappings.namespace.disable_cancel': 'Keep enabled',
  'keys.mappings.namespace.disable_close': 'Close confirmation',
  'keys.mappings.conflicts.title': 'Conflicting short model names',
  'keys.mappings.conflicts.description':
    'These aliases point to more than one model and are excluded from routing. Disable one automatic namespace or delete a conflicting manual mapping; full namespace/model names still work.',
  'keys.mappings.conflicts.delete_manual': 'Delete manual mapping',
  'keys.mappings.conflicts.delete_title': 'Delete the manual mapping?',
  'keys.mappings.conflicts.delete_description':
    'The manual mapping for {alias} will be deleted. If the remaining automatic candidates are unambiguous, the alias will resolve again.',
  'keys.mappings.conflicts.delete_confirm': 'Delete manual mapping',
  'keys.mappings.conflicts.delete_cancel': 'Keep mapping',
  'keys.mappings.conflicts.delete_close': 'Close confirmation',
  'keys.mappings.delete': 'Delete',
  'keys.mappings.add_title': 'Add mapping',
  'keys.mappings.edit_title': 'Change target for {alias}',
  'keys.mappings.alias_label': 'Alias',
  'keys.mappings.alias_placeholder': 'claude-sonnet-4-5',
  'keys.mappings.alias_hint':
    'The name your client sends in the model field. Must not contain "/".',
  'keys.mappings.alias_required': 'Enter an alias',
  'keys.mappings.alias_no_slash': 'Aliases cannot contain "/"',
  'keys.mappings.namespace_label': 'Namespace',
  'keys.mappings.namespace_placeholder': 'Choose a namespace',
  'keys.mappings.sku_label': 'Model',
  'keys.mappings.sku_placeholder': 'Choose a model',
  'keys.mappings.sku_pick_namespace_first': 'Choose a namespace first',
  'keys.mappings.sku_required': 'Choose a model',
  'keys.mappings.submit_add': 'Add mapping',
  'keys.mappings.submit_save': 'Save change',
  'keys.mappings.cancel_edit': 'Cancel edit',
  'keys.mappings.toast.added': 'Mapping added',
  'keys.mappings.toast.updated': 'Mapping updated',
  'keys.mappings.toast.deleted': 'Mapping deleted',
  'keys.mappings.catalog_error':
    'Could not load the model catalog — adding mappings is unavailable.',

  // ── Service status page (/access/status) ──────────────────────
  'accessStatus.title': 'Service status',
  'accessStatus.subtitle': 'Separate local setup issues from platform service availability.',
  'accessStatus.error': 'Could not load service status.',
  'accessStatus.retry': 'Retry',
  'accessStatus.kpi.overall': 'Overall state',
  'accessStatus.kpi.overall_hint': '{healthy}/{total} channels healthy',
  'accessStatus.kpi.health': 'Health check',
  'accessStatus.kpi.health_hint': 'Platform API probe',
  'accessStatus.health.ok': 'OK',
  'accessStatus.health.down': 'Down',
  'accessStatus.kpi.updated': 'Updated',
  'accessStatus.kpi.updated_hint': 'Last refresh of status data',
  'accessStatus.state.healthy': 'Healthy',
  'accessStatus.state.busy': 'Busy',
  'accessStatus.state.unavailable': 'Unavailable',
  'accessStatus.vendors.caption': 'Provider status',
  'accessStatus.column.vendor': 'Provider',
  'accessStatus.column.channels': 'Healthy channels',
  'accessStatus.column.state': 'State',
  'accessStatus.events.caption': 'Recent events',
  'accessStatus.events.empty': 'No state transitions yet.',
  // #1112 — uptime-timeline view (namespace history from /v1/status).
  'accessStatus.kpi.overall_hint_ns': '{healthy}/{total} services healthy',
  'accessStatus.timeline.caption': 'Service availability',
  'accessStatus.timeline.window': 'Last {hours} hours',
  'accessStatus.timeline.uptime': 'uptime',
  'accessStatus.timeline.window_start': '24 hours ago',
  'accessStatus.timeline.window_now': 'now',
  'accessStatus.timeline.block_title': '{start}–{end} · {rate}% success · {count} requests',
  'accessStatus.timeline.block_no_data': '{start}–{end} · no requests',
  'accessStatus.timeline.skus_show': 'Show {count} model details',
  'accessStatus.timeline.skus_hide': 'Hide model details',

  // ── Integration docs page (/access/docs) ──────────────────────
  'accessDocs.title': 'Integration docs',
  'accessDocs.subtitle':
    'From creating a key and understanding model names and mappings to wiring up Claude Code, Codex, and graphical clients.',
  'accessDocs.toast.copied': 'Copied',
  'accessDocs.copy': 'Copy',
  'accessDocs.toc.aria': 'Integration docs contents',
  'accessDocs.fact.anthropic.title': 'Anthropic Base URL',
  'accessDocs.fact.anthropic.note': 'no /v1',
  'accessDocs.fact.openai.title': 'OpenAI Base URL',
  'accessDocs.fact.openai.note': 'must include /v1',
  'accessDocs.fact.auth.title': 'Authentication',
  'accessDocs.fact.auth.note': 'full key shown only once',
  'accessDocs.fact.copy': 'Copy',
  'accessDocs.section.prepare.label': 'Prepare',
  'accessDocs.section.prepare.title': 'Before you integrate',
  'accessDocs.section.models.label': 'Model names',
  'accessDocs.section.models.title': 'Model names and mappings',
  'accessDocs.section.createKey.label': 'Create a key',
  'accessDocs.section.createKey.title': 'Create an API key',
  'accessDocs.section.claudeCode.label': 'Claude Code',
  'accessDocs.section.claudeCode.title': 'Claude Code setup',
  'accessDocs.section.codex.label': 'Codex',
  'accessDocs.section.codex.title': 'Codex setup',
  'accessDocs.section.ccSwitch.label': 'CC Switch',
  'accessDocs.section.ccSwitch.title': 'CC Switch setup',
  'accessDocs.section.openclaw.label': 'OpenClaw',
  'accessDocs.section.openclaw.title': 'OpenClaw setup',
  'accessDocs.section.clients.label': 'Graphical clients',
  'accessDocs.section.clients.title': 'Graphical client setup',
  'accessDocs.section.claudeDesktop.label': 'Claude Desktop',
  'accessDocs.section.claudeDesktop.title': 'Claude Desktop setup',
  'accessDocs.section.vscode.label': 'VS Code',
  'accessDocs.section.vscode.title': 'Using it in VS Code',
  'accessDocs.section.images.label': 'Images',
  'accessDocs.section.images.title': 'Image generation and editing',
  'accessDocs.images.note':
    'The standard OpenAI Images protocol: `POST /v1/images/generations` to generate and ' +
    '`POST /v1/images/edits` to edit (multipart upload supported). The official SDKs work ' +
    'unchanged — point base_url at the OpenAI-compatible address above and pass the full ' +
    'image SKU name from your catalog. Generation is far slower than a text request and SDK ' +
    'default timeouts are usually too short — raise yours explicitly.',
  'accessDocs.images.sdk': 'Official Python SDK (generate + edit)',
  'accessDocs.images.curl': 'curl (generate + edit)',
  'accessDocs.images.table.generate': 'POST /v1/images/generations parameters',
  'accessDocs.images.table.edit': 'POST /v1/images/edits parameters',
  'accessDocs.images.column.param': 'Parameter',
  'accessDocs.images.column.required': 'Required',
  'accessDocs.images.column.default': 'Default',
  'accessDocs.images.column.behaviour': 'Actual behaviour',
  'accessDocs.images.required.yes': 'Yes',
  'accessDocs.images.required.no': 'No',
  'accessDocs.images.default.none': 'None',
  'accessDocs.images.param.model':
    'The full image SKU name from your catalog (`namespace/model`). A bare name works only if ' +
    'that key has a model mapping for it.',
  'accessDocs.images.param.prompt': 'What to generate, or what to change.',
  'accessDocs.images.param.quality':
    'Accepted but not honoured. The response reports the tier that was actually used.',
  'accessDocs.images.param.n': 'Only 1 is supported. Anything else is a 400, and costs nothing.',
  'accessDocs.images.param.response_format':
    'Only `b64_json`. `url` is a 400 — this route serves no hosted links.',
  'accessDocs.images.param.background': 'Only `opaque`. `transparent` is a 400.',
  'accessDocs.images.param.size':
    'Passed through as an aspect-ratio hint, not a constraint. The `size` in the response is what ' +
    'was DELIVERED and may differ from what you asked for. Read the response; do not assume it ' +
    'echoes the request.',
  'accessDocs.images.param.output_format': '`png` / `jpeg` / `webp`.',
  'accessDocs.images.param.output_compression':
    'Compression quality; applies to `jpeg` / `webp` only.',
  'accessDocs.images.param.image':
    'The image to change; several reference images are allowed. Multipart file upload and base64 ' +
    'in JSON both work. The request body is capped at 12 MB.',
  'accessDocs.images.param.mask':
    'Marks the region to repaint. The mask guides the model rather than cutting exactly.',
  'accessDocs.images.billing':
    'Billing follows the `usage` block on the response: `output_tokens_details.image_tokens` is ' +
    "the image output and `input_tokens` is the input side, both priced at that image SKU's " +
    'rate — so every charge reconciles against Usage and Billing line by line.',
  'accessDocs.section.videos.label': 'Videos',
  'accessDocs.section.videos.title': 'Grok video generation',
  'accessDocs.videos.note':
    'xAI video generation: `POST /v1/videos/generations` submits a job and ' +
    '`GET /v1/videos/{request_id}` polls it. Pass the full video SKU name from your catalog ' +
    '(`namespace/model`); do not send the bare official name. The Python `xai-sdk` `api_host` is ' +
    'the gateway origin WITHOUT `/v1` — the same host as the Anthropic entry above. If the SDK ' +
    'names that field differently from upstream, the test is whether the call lands on ' +
    '`POST /v1/videos/generations`. A successful create returns only `{ "request_id": "…" }`; that ' +
    'is our wrapping id, and the upstream `vid_` never appears in the response. On `done`, ' +
    '`video.url` is a temporary mp4 — download it immediately if you need to keep it. Generation ' +
    'is an order of magnitude slower than a text request, and SDK default timeouts are usually ' +
    'too short — raise yours explicitly.',
  'accessDocs.videos.sdk': 'Python xai-sdk (create; SDK polls)',
  'accessDocs.videos.curl': 'curl (create + poll)',
  'accessDocs.videos.table.generate': 'POST /v1/videos/generations parameters',
  'accessDocs.videos.column.param': 'Parameter',
  'accessDocs.videos.column.required': 'Required',
  'accessDocs.videos.column.default': 'Default',
  'accessDocs.videos.column.behaviour': 'Actual behaviour',
  'accessDocs.videos.required.yes': 'Yes',
  'accessDocs.videos.required.no': 'No',
  'accessDocs.videos.default.none': 'None',
  'accessDocs.videos.param.model':
    'The full video SKU name from your catalog (`xai/grok-imagine-video` or ' +
    '`xai/grok-imagine-video-1.5`). A bare name works only if that key has a model mapping for it.',
  'accessDocs.videos.param.prompt':
    'Text-to-video description. An empty string is a 400, and costs nothing.',
  'accessDocs.videos.param.resolution':
    'Only `480p` / `720p`. Omitting it is a 400. `1080p` is a 400 with ' +
    '"This resolution is not available." and costs nothing. Any other value is also a 400.',
  'accessDocs.videos.param.duration':
    'Positive integer seconds, range 1–15. Omit and the upstream default of 8 seconds applies. ' +
    'A non-integer or non-positive value is a 400, and costs nothing.',
  'accessDocs.videos.param.image':
    'Image-to-video is not wired. Sending `image`, `image_url`, or `images` is a 400, and costs nothing.',
  'accessDocs.videos.param.reference':
    'Reference-to-video is not wired. Sending `reference_images`, `reference_audios`, or `voices` ' +
    'is a 400, and costs nothing.',
  'accessDocs.videos.param.unforwarded':
    'Not forwarded this round. Sending them is the same as omitting them.',
  'accessDocs.videos.param.idempotency':
    'Request header. The gateway mints one if omitted. Reusing a key with a different body is a 409.',
  'accessDocs.videos.poll':
    '`GET /v1/videos/{id}` reports `status` as `pending`, `done`, `failed`, or `expired`. A `done` ' +
    'payload carries `video.url` (a temporary mp4 — download it immediately) and `video.duration` ' +
    '(the delivered length; billing uses this, not the duration you requested). One in-flight job ' +
    'per account per resolution tier; anything above that is a 429 `TOO_MANY_TASKS_IN_FLIGHT`.',
  'accessDocs.videos.billing':
    'Billed as delivered seconds × the per-second price of the resolution tier (`480p` / `720p`; ' +
    'list prices are on the pricing page). No delivery means no charge: a 4xx, `failed` / ' +
    '`expired`, or a success with no usable url or duration. Every charge reconciles against the ' +
    'pricing page line by line.',
  'accessDocs.section.verify.label': 'Verify',
  'accessDocs.section.verify.title': 'Verify and troubleshoot',
  'accessDocs.prepare.console': 'Console',
  'accessDocs.prepare.keys_page': 'Key management',
  'accessDocs.prepare.models': 'Model list',
  'accessDocs.prepare.requests': 'Request logs',
  'accessDocs.prepare.warning':
    'The most common mistake is a wrong Base URL: the Anthropic endpoint must NOT include /v1, while the OpenAI-compatible endpoint MUST include /v1.',
  'accessDocs.prepare.anthropic_entry': 'Anthropic entry · no /v1',
  'accessDocs.prepare.anthropic_fit':
    'Claude-series models go here: Claude Code, Claude Desktop, and other Anthropic-protocol tools. Some Claude namespaces accept Claude Code clients only.',
  'accessDocs.prepare.openai_entry': 'OpenAI entry · with /v1',
  'accessDocs.prepare.openai_fit':
    'GPT-series models go here: OpenAI-compatible tools such as Codex, OpenClaw, Cherry Studio, ChatBox, and LobeChat.',
  'accessDocs.prepare.env_check': 'Command-line environment check',
  'accessDocs.models.intro':
    'Model names come in two forms: full names with a "/" (namespace/model, e.g. {example}) work on any key out of the box; short names without a "/" are looked up only in the key model-mapping table and return 404 without a matching mapping.',
  'accessDocs.models.row.canonical': 'Full model name',
  'accessDocs.models.row.canonical_value':
    'The "namespace/model" form, exactly as shown on the Model pricing page — copy and use as-is. Recommended for all new integrations.',
  'accessDocs.models.row.alias': 'Short name (alias)',
  'accessDocs.models.row.alias_value':
    'For clients that can only send bare model names. Add an "alias → target model" mapping on the key first; aliases must not contain "/".',
  'accessDocs.models.row.manage': 'Managing mappings',
  'accessDocs.models.row.manage_value':
    'On the Keys page, "Model mappings" can map every current model in one namespace at once or keep a namespace mapped automatically as new models appear. Conflicting short names are shown and excluded until you disable an automatic namespace or remove the conflicting manual mapping.',
  'accessDocs.models.row.pricing': 'Price differences',
  'accessDocs.models.row.pricing_value':
    'The same model can exist under several namespaces at different rate multipliers — picking a model name is also picking a price. See the Model pricing page.',
  'accessDocs.models.list_snippet': 'List the models this key can call',
  'accessDocs.models.source_of_truth':
    'GET /v1/models is the source of truth for each key: it lists every full model name visible to you plus the aliases configured on that key.',
  'accessDocs.models.cc_lane':
    'Some Claude namespaces only accept requests from the Claude Code client — calling them via curl or graphical clients returns an upstream error. Use those models from Claude Code, or pick a namespace that supports generic API calls.',
  'accessDocs.createKey.step1.title': 'Open the Keys page',
  'accessDocs.createKey.step1.body':
    'Sign in to the console, go to the Keys page, and click Create key.',
  'accessDocs.createKey.step2.title': 'Optionally pre-seed mappings and limits',
  'accessDocs.createKey.step2.body':
    'Keys are not provider-scoped. Optionally pick a namespace to atomically seed its current short-name mappings, then enable automatic mapping later if future models should appear without manual work. You can also set spend limits.',
  'accessDocs.createKey.step3.title': 'Copy and store it',
  'accessDocs.createKey.step3.body':
    'The full key is shown only once. Name keys after their tool to keep usage traceable.',
  'accessDocs.createKey.row.name': 'Key name',
  'accessDocs.createKey.row.name_value':
    'Name keys after the tool, e.g. claude-code-mac, codex-work, cherry-studio.',
  'accessDocs.createKey.row.template': 'Namespace template',
  'accessDocs.createKey.row.template_value':
    'Optional. Atomically seeds one "alias = model name" mapping per current model in the chosen namespace. In Model mappings, automatic mode keeps future models in sync and reports duplicate aliases before they can route ambiguously.',
  'accessDocs.createKey.row.limits': 'Spend limits',
  'accessDocs.createKey.row.limits_value':
    'Optional CNY caps per request, per day, per week, and per month.',
  'accessDocs.createKey.hint':
    'One key can call every model visible to you — Claude and GPT alike. Create separate keys per tool or member for usage attribution. If the dashboard balance is 0, ask the team owner or an admin to top up first.',
  'accessDocs.createKey.open_keys': 'Go create a key',
  'accessDocs.platform.aria': 'Operating system',
  'accessDocs.platform.macos': 'macOS / Linux',
  'accessDocs.platform.windows': 'Windows',
  'accessDocs.claudeCode.intro':
    'Claude Code is built for reading, changing, and refactoring code from the terminal. To route it through Nadoutong, use the Anthropic entry and set the model to a full model name (namespace/model).',
  'accessDocs.claudeCode.install': 'Install and confirm the version',
  'accessDocs.claudeCode.temp_env': 'Temporary environment variables',
  'accessDocs.claudeCode.long_term': 'Persistent configuration',
  'accessDocs.claudeCode.row.model': 'Model setting',
  'accessDocs.claudeCode.row.model_value':
    'Set ANTHROPIC_MODEL to a full model name from the Model pricing page; optionally set ANTHROPIC_SMALL_FAST_MODEL for the background model. If unset, the default model names Claude Code sends need same-named mappings on the key.',
  'accessDocs.claudeCode.row.first_test': 'First test',
  'accessDocs.claudeCode.row.first_test_value':
    'Open a small project directory and give Claude Code a read-only task first.',
  'accessDocs.claudeCode.row.prompt': 'Suggested prompt',
  'accessDocs.claudeCode.row.prompt_value':
    'Do not modify any files yet — read the project structure and tell me how to start this project.',
  'accessDocs.claudeCode.row.url_mistake': 'Common URL mistake',
  'accessDocs.claudeCode.row.url_mistake_value': 'Do not append /v1 to ANTHROPIC_BASE_URL.',
  'accessDocs.claudeCode.disable_betas': 'Disable experimental beta fields',
  'accessDocs.protocol.aria': 'Protocol',
  'accessDocs.protocol.anthropic': 'Anthropic · Claude Code',
  'accessDocs.protocol.openai': 'OpenAI · Codex',
  'accessDocs.codex.intro':
    'Codex-style tools use the OpenAI-compatible interface. To route them through Nadoutong, set the Base URL to the OpenAI endpoint, use your Nadoutong API key, and set the model to a full model name (e.g. {example}).',
  'accessDocs.codex.env.claude': 'Claude Code environment variables',
  'accessDocs.codex.env.codex': 'Codex environment variables',
  'accessDocs.codex.row.provider': 'Provider',
  'accessDocs.codex.row.provider_value': 'OpenAI Compatible / Custom OpenAI',
  'accessDocs.codex.row.base_url': 'Base URL / API Host',
  'accessDocs.codex.row.api_key': 'API Key',
  'accessDocs.codex.row.api_key_value': 'Your Nadoutong API key, prefixed sk-ndt-.',
  'accessDocs.codex.row.model': 'Model',
  'accessDocs.ccSwitch.intro':
    'CC Switch manages providers for Claude Code, Codex, OpenClaw, and similar tools in one place. Once set up, you can flip between the Nadoutong Claude and OpenAI Compatible configs locally.',
  'accessDocs.ccSwitch.install': 'Install on macOS',
  'accessDocs.ccSwitch.claude_panel': 'Claude Code provider',
  'accessDocs.ccSwitch.openai_panel': 'Codex / OpenAI provider',
  'accessDocs.ccSwitch.row.provider_name': 'Provider name',
  'accessDocs.ccSwitch.row.claude_provider_name': 'Nadoutong Claude',
  'accessDocs.ccSwitch.row.openai_provider_name': 'Nadoutong OpenAI Compatible',
  'accessDocs.ccSwitch.row.app': 'Tool / app',
  'accessDocs.ccSwitch.row.claude_app': 'Claude Code',
  'accessDocs.ccSwitch.row.openai_app': 'Codex / OpenClaw / OpenAI Compatible',
  'accessDocs.ccSwitch.row.protocol': 'API protocol',
  'accessDocs.ccSwitch.row.claude_protocol': 'Anthropic Compatible',
  'accessDocs.ccSwitch.row.openai_protocol': 'OpenAI Compatible',
  'accessDocs.ccSwitch.row.base_url': 'Base URL',
  'accessDocs.ccSwitch.row.api_key': 'API Key',
  'accessDocs.ccSwitch.row.claude_key': 'Your Nadoutong API key (sk-ndt- prefix)',
  'accessDocs.ccSwitch.row.openai_key':
    'Your Nadoutong API key (sk-ndt- prefix; the same key as Claude works)',
  'accessDocs.ccSwitch.row.model': 'Model',
  'accessDocs.ccSwitch.checklist.title': 'If switching does not take effect, check',
  'accessDocs.ccSwitch.checklist.1':
    'The provider currently selected in CC Switch is the right one.',
  'accessDocs.ccSwitch.checklist.2':
    'The terminal was opened before you switched — open a new terminal after switching.',
  'accessDocs.ccSwitch.checklist.3': 'Whether Claude Code / Codex needs a restart.',
  'accessDocs.ccSwitch.checklist.4':
    'The Base URL matches the protocol: Anthropic without /v1, OpenAI with /v1.',
  'accessDocs.ccSwitch.checklist.5': 'New calls appear in the console request logs.',
  'accessDocs.openclaw.intro':
    'OpenClaw is configured as an OpenAI-compatible provider. Point the provider at the Nadoutong OpenAI Base URL and set the model to a full model name.',
  'accessDocs.openclaw.snippet': 'OpenAI-compatible provider',
  'accessDocs.openclaw.row.base_url': 'Base URL',
  'accessDocs.openclaw.row.api_key': 'API Key',
  'accessDocs.openclaw.row.api_key_value': 'Your Nadoutong API key.',
  'accessDocs.openclaw.row.model': 'Model',
  'accessDocs.openclaw.row.verify': 'Verification prompt',
  'accessDocs.openclaw.row.verify_value': 'Reply with: OpenClaw is connected.',
  'accessDocs.clients.column.tool': 'Tool',
  'accessDocs.clients.column.vendor': 'Protocol',
  'accessDocs.clients.column.base': 'Base URL',
  'accessDocs.clients.column.notes': 'Notes',
  'accessDocs.clients.base_both': 'both protocols',
  'accessDocs.clients.tool.claude_code': 'Terminal AI coding; uses the Anthropic entry.',
  'accessDocs.clients.tool.codex': 'OpenAI-compatible CLI; the responses wire API is recommended.',
  'accessDocs.clients.tool.cc_switch':
    'Manages providers for Claude Code, Codex, OpenClaw, and more.',
  'accessDocs.clients.tool.openclaw': 'Configure as an OpenAI Compatible provider.',
  'accessDocs.clients.tool.cherry_studio':
    'Desktop client — pick models that support generic API calls (some Claude namespaces are Claude Code-only).',
  'accessDocs.clients.tool.chatbox':
    'Lightweight chat client; set API Host to the OpenAI-compatible URL.',
  'accessDocs.clients.tool.lobechat':
    'Self-hosted or desktop — both use the OpenAI-compatible endpoint.',
  'accessDocs.clients.tool.claude_desktop':
    'Third-party gateway entry; if calls fail with upstream errors, use Claude Code instead.',
  'accessDocs.clients.key_name': 'Suggested key name: {name}',
  'accessDocs.clients.row.entry': 'Settings entry',
  'accessDocs.clients.row.provider_type': 'Provider type',
  'accessDocs.clients.row.provider_type_value': 'OpenAI Compatible / custom OpenAI',
  'accessDocs.clients.row.api_host': 'API host',
  'accessDocs.clients.row.model': 'Model',
  'accessDocs.clients.row.test': 'Test',
  'accessDocs.clients.cherry.entry': 'Model services / provider management',
  'accessDocs.clients.cherry.test': 'Confirm the connection works in one sentence.',
  'accessDocs.clients.cherry.note':
    'Some Claude namespaces only accept the Claude Code client and fail with upstream errors in Cherry Studio; use the full model names shown on the Model pricing page.',
  'accessDocs.clients.chatbox.entry': 'Model provider / API settings',
  'accessDocs.clients.chatbox.test': 'Reply with: ChatBox is configured.',
  'accessDocs.clients.chatbox.note':
    'Enter the full OpenAI Base URL as the API Host — do not drop or duplicate /v1.',
  'accessDocs.clients.lobechat.entry': 'OpenAI / OpenAI Compatible',
  'accessDocs.clients.lobechat.test': 'Reply with: LobeChat is configured.',
  'accessDocs.clients.lobechat.note':
    'For team use, create separate keys per member or project so spend stays traceable.',
  'accessDocs.clients.lobechat_env': 'LobeChat self-hosted environment variables',
  'accessDocs.claudeDesktop.intro':
    'This configures the graphical third-party gateway in Claude Desktop — not the Claude Code terminal variables. If calls fail with upstream errors, the chosen model is Claude Code-only; integrate via Claude Code instead.',
  'accessDocs.claudeDesktop.macos.title': 'macOS setup',
  'accessDocs.claudeDesktop.macos.1': 'Open Help → Troubleshooting → Enable Developer Mode.',
  'accessDocs.claudeDesktop.macos.2':
    'After the app restarts, keep Claude Desktop selected on the welcome screen.',
  'accessDocs.claudeDesktop.macos.3': 'Open Developer → Configure Provider.',
  'accessDocs.claudeDesktop.macos.4':
    'Set Gateway base URL to {baseUrl} and Gateway API key to your Nadoutong API key.',
  'accessDocs.claudeDesktop.macos.5':
    'Click Apply locally, choose Relaunch now, then Continue with gateway after the restart.',
  'accessDocs.claudeDesktop.windows.title': 'Windows setup',
  'accessDocs.claudeDesktop.windows.1': 'Stay on the welcome or email-entry screen.',
  'accessDocs.claudeDesktop.windows.2':
    'Press Tab to reach the top-left menu icon, then press Enter to open the menu.',
  'accessDocs.claudeDesktop.windows.3':
    'Choose Developer → Configure third-party interface or Configure Provider.',
  'accessDocs.claudeDesktop.windows.4': 'Set Gateway base URL to {baseUrl} — without /v1.',
  'accessDocs.claudeDesktop.windows.5': 'After Apply locally, restart when prompted.',
  'accessDocs.claudeDesktop.row.no_menu': 'Developer menu missing',
  'accessDocs.claudeDesktop.row.no_menu_value':
    'Enable Developer Mode first and confirm Claude Desktop is the selected app.',
  'accessDocs.claudeDesktop.row.no_gateway': 'Continue with gateway never appears',
  'accessDocs.claudeDesktop.row.no_gateway_value':
    'Confirm you clicked Apply locally and relaunched.',
  'accessDocs.claudeDesktop.row.no_connect': 'Cannot connect after configuring',
  'accessDocs.claudeDesktop.row.no_connect_value':
    'Check that the Gateway base URL and key are complete.',
  'accessDocs.claudeDesktop.row.rejected': 'Requests fail with upstream errors',
  'accessDocs.claudeDesktop.row.rejected_value':
    'The namespace of the chosen model only accepts the Claude Code client — switch to Claude Code, or pick a model that supports generic clients.',
  'accessDocs.vscode.intro':
    'VS Code itself is not a model client, but it is a handy project entry point — run Claude Code or Codex in its built-in terminal. If an extension supports custom providers, fill in the matching protocol.',
  'accessDocs.vscode.snippet': 'VS Code built-in terminal example',
  'accessDocs.vscode.row.openai_base': 'OpenAI-compatible Base URL',
  'accessDocs.vscode.row.anthropic_base': 'Anthropic-compatible Base URL',
  'accessDocs.vscode.row.openai_models': 'OpenAI models',
  'accessDocs.vscode.row.openai_models_value':
    'Full model names (namespace/model) as shown on the Model pricing page.',
  'accessDocs.vscode.row.anthropic_models': 'Anthropic models',
  'accessDocs.vscode.row.anthropic_models_value':
    'Full model names as well; some Claude namespaces are Claude Code-only, so extension calls fail with upstream errors.',
  'accessDocs.verify.note':
    'Verify the key and its callable models first with GET /v1/models (see the Model names section), then send a real request — replace the model placeholder in the example with a name returned by /v1/models. Some Claude namespaces only accept the Claude Code client, so a plain curl against those models fails with an upstream error, which is expected.',
  'accessDocs.verify.anthropic_curl': 'Anthropic /v1/messages',
  'accessDocs.verify.openai_curl': 'OpenAI /v1/chat/completions',
  'accessDocs.verify.column.symptom': 'Symptom',
  'accessDocs.verify.column.reason': 'Likely cause',
  'accessDocs.verify.column.action': 'What to do',
  'accessDocs.verify.error1.symptom': '401 / unauthorized',
  'accessDocs.verify.error1.reason': 'Wrong key, copied with whitespace, or key revoked.',
  'accessDocs.verify.error1.action':
    'Re-check the key (sk-ndt- prefix); revoke and recreate it if needed.',
  'accessDocs.verify.error2.symptom': '404 model not found',
  'accessDocs.verify.error2.reason':
    'A short name without a mapping, a misspelled name, or a model not visible to you.',
  'accessDocs.verify.error2.action':
    'Use the full model name (namespace/model) from the Model pricing page; add a mapping on the key for short names; GET /v1/models lists every name this key can call.',
  'accessDocs.verify.error3.symptom': 'Third-party client fails on claude models',
  'accessDocs.verify.error3.reason':
    'The namespace of that model only accepts the Claude Code client.',
  'accessDocs.verify.error3.action':
    'Switch to Claude Code, or pick a model that supports generic API calls (see the Model pricing page).',
  'accessDocs.verify.error4.symptom': 'Endpoint unreachable / path 404',
  'accessDocs.verify.error4.reason': 'Wrong Base URL.',
  'accessDocs.verify.error4.action': 'Anthropic Base URL without /v1; OpenAI Base URL with /v1.',
  'accessDocs.verify.error5.symptom': 'No calls in the console',
  'accessDocs.verify.error5.reason': 'Requests are not going through Nadoutong.',
  'accessDocs.verify.error5.action':
    'Check the active provider, environment variables, or config file.',
  'accessDocs.verify.error6.symptom': '402 insufficient balance',
  'accessDocs.verify.error6.reason': 'Team balance is 0.',
  'accessDocs.verify.error6.action': 'Ask the team owner or an admin to top up.',
  'accessDocs.verify.error7.symptom': '403 budget limited',
  'accessDocs.verify.error7.reason': 'A key spend limit or member monthly limit was hit.',
  'accessDocs.verify.error7.action':
    'Adjust the relevant limit or wait for the period to reset; key limits are editable on the Keys page.',
  'accessDocs.verify.link.usage': 'Usage',
  'accessDocs.verify.link.usage_detail': 'Spend by key and model',
  'accessDocs.verify.link.requests': 'Request logs',
  'accessDocs.verify.link.requests_detail': 'Every request and its errors',
  'accessDocs.verify.link.pricing': 'Model pricing',
  'accessDocs.verify.link.pricing_detail': 'Confirm the available models',
  'accessDocs.verify.security':
    'Use API keys only in your own tools. Never share them, and never paste them into public repos, screenshots, or group chats. Create one key per tool so a leak only means revoking and recreating that one.',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.title': 'Usage',
  'usage.subtitle.admin':
    'Review enterprise request, token, model, and key metadata from usage logs.',
  'usage.subtitle.member': 'Review your own request, token, model, and key metadata.',
  'usage.scope.admin': 'Enterprise scope',
  'usage.scope.member': 'Personal scope',
  'usage.window.label': 'Query window',
  'usage.window.pending': 'Waiting for usage window',
  'usage.window.source': 'Source: usage logs metadata',
  'usage.range.aria_label': 'Time range',
  'usage.range.today': 'Today',
  'usage.range.7d': 'Last 7 days',
  'usage.range.30d': 'Last 30 days',
  'usage.range.90d': 'Last 90 days',
  'usage.range.month': 'This month',
  'usage.range.prev_month': 'Previous month',
  'usage.range.next_month': 'Next month',
  'usage.legend.input': 'Input tokens',
  'usage.legend.output': 'Output tokens',
  'usage.summary.requests': 'Requests',
  'usage.summary.tokens_in': 'Tokens in',
  'usage.summary.tokens_out': 'Tokens out',
  'usage.summary.input_tokens': 'Input tokens',
  'usage.summary.output_tokens': 'Output tokens',
  'usage.summary.cache_write': 'Cache write',
  'usage.summary.cache_read': 'Cache read',
  'usage.summary.blended_spend': 'Blended spend',
  'usage.summary.spend': 'Spend',
  'usage.summary.cache_hit': 'Cache hit',
  'usage.summary.cost': 'Cost',
  'usage.summary.range': 'Range: {label}',
  'usage.summary.cost_unit': 'Shown in CNY',
  'usage.summary.requests_hint': '{count} requests',
  'usage.summary.cache_read_suffix': '+ {tokens} cached input tokens',
  'usage.summary.cache_write_suffix': '+ {tokens} cached write tokens',
  'usage.technical.summary': 'View the extra token volume caused by cache hits and cache writes.',
  'usage.technical.cache_read': 'Cache-hit tokens',
  'usage.technical.cache_write': 'Cache-write tokens',
  'usage.chart.title': 'Request trend · {label}',
  'usage.chart.subtitle': 'Hourly request volume for short-window spikes.',
  'usage.chart.loading': 'Loading chart…',
  'usage.chart.error': 'Could not load chart',
  'usage.model_mix.title': 'By model',
  'usage.model_mix.subtitle': 'Token share from recent request metadata.',
  'usage.model_mix.loading': 'Loading model mix…',
  'usage.model_mix.error': 'Could not load model mix',
  'usage.model_mix.empty': 'No model metadata in this range.',
  'usage.model_mix.requests': '{count} requests',
  'usage.routing.title': 'Service health',
  'usage.routing.subtitle':
    'Recent request metadata only; prompts and responses are not collected.',
  'usage.routing.error': 'Could not load service health',
  'usage.routing.sampled_requests': 'Sampled requests',
  'usage.routing.success_rate': 'Success rate',
  'usage.routing.avg_latency': 'Avg latency',
  'usage.routing.rate_limited': 'Rate-limited',
  'usage.routing.failed_requests': 'Failed requests',
  'usage.by_key.title': 'By key · {label}',
  'usage.by_key.subtitle': 'Requests, tokens, cache, and cost grouped by API key.',
  'usage.by_key.loading': 'Loading breakdown…',
  'usage.by_key.error': 'Could not load breakdown',
  'usage.by_key.empty': 'No usage in this range.',
  'usage.by_key.count': '{count} keys',
  'usage.by_key.unnamed_key': 'Unnamed key',
  'usage.by_key.unknown_key': 'Unknown key',
  'usage.by_key.column.key_name': 'Key name',
  'usage.by_key.column.model': 'Model',
  'usage.by_key.column.requests': 'Requests',
  'usage.by_key.column.input_tokens': 'Input tokens',
  'usage.by_key.column.output_tokens': 'Output tokens',
  'usage.by_key.column.cache_read': 'Cache hit',
  'usage.by_key.column.cache_write': 'Cache write',
  'usage.by_key.column.cost': 'Cost',
  'usage.requests_count': '{count} requests',
  'usage.succeeded_count': '{count} succeeded',
  'usage.pagination.previous': 'Previous',
  'usage.pagination.next': 'Next',
  'usage.metric.total': 'total',
  'usage.metric.in': 'In',
  'usage.metric.out': 'Out',
  'usage.metric.write': 'Write',
  'usage.metric.read': 'Read',
  'usage.error.load': 'Could not load usage data.',
  'usage.retry': 'Retry',
  'usage.admin.title': 'Token usage',
  'usage.admin.subtitle': 'Aggregate model consumption across every member, key, and request.',
  'usage.admin.export.idle': 'Export CSV',
  'usage.admin.export.working': 'Exporting...',
  'usage.admin.export.error': 'CSV export failed.',
  'usage.admin.export.done': 'CSV exported',
  'usage.admin.window.meta': '{from} - {to} · refreshed {time}',
  'usage.admin.filter.model': 'Model',
  'usage.admin.filter.api_key': 'API key',
  'usage.admin.filter.department': 'Org unit',
  'usage.admin.filter.member': 'Member',
  'usage.admin.filter.all_models': 'All models',
  'usage.admin.filter.model_all_vendors': '{model} · all vendors',
  'usage.admin.filter.all_keys': 'All keys',
  'usage.admin.filter.all_departments': 'All org units',
  'usage.admin.filter.all_members': 'All members',
  'usage.admin.filter.caption': 'Filters',
  'usage.admin.filter.revoked_suffix': ' (revoked)',
  'usage.admin.filter.show_revoked_keys': 'Show revoked keys',
  'usage.admin.group_by.model': 'Group by model',
  'usage.admin.group_by.department': 'Group by org unit',
  'usage.admin.group_by.member': 'Group by member',
  'usage.admin.group_by_value.model': 'model',
  'usage.admin.group_by_value.department': 'org unit',
  'usage.admin.group_by_value.member': 'member',
  'usage.admin.granularity.day': 'day',
  'usage.admin.granularity.week': 'week',
  'usage.admin.granularity.month': 'month',
  'usage.admin.granularity.aria': 'Granularity',
  'usage.admin.summary.cache_write_detail': 'prompt cache creation',
  'usage.admin.summary.cache_read_detail': 'prompt cache reuse',
  'usage.admin.summary.cost_detail': 'usage logs cost metadata',
  'usage.admin.summary.seats_with_usage': 'Seats with usage',
  'usage.admin.summary.seats_roster_detail': '{seats} seats on roster · {keys} keys not revoked',
  'usage.admin.summary.seats_roster_only': '{seats} seats on roster',
  'usage.admin.trend.title': 'Token usage trend',
  'usage.admin.trend.subtitle': 'Stacked by {groupBy}',
  'usage.admin.trend.total': 'total · {tokens} tokens',
  'usage.admin.trend.metric_aria': 'Trend metric',
  'usage.admin.empty.window': 'No usage in this window.',
  'usage.admin.model_mix.subtitle': 'Share of total tokens in the selected window.',
  'usage.admin.model_mix.empty': 'No model usage in this window.',
  'usage.admin.model_mix.io': 'in {input} · out {output}',
  'usage.admin.department_mix.title': 'Org unit mix',
  'usage.admin.department_mix.subtitle': 'Tokens consumed per org unit this period.',
  'usage.admin.department_mix.empty': 'No org unit usage in this window.',
  'usage.admin.department_mix.unassigned': 'Unassigned',
  'usage.admin.service.title': 'Service health',
  'usage.admin.service.subtitle': 'Request quality based on billing metadata.',
  'usage.admin.service.all_green': 'All green',
  'usage.admin.service.requests_evaluated': 'Requests evaluated',
  'usage.admin.service.cache_hit_rate': 'Cache hit rate',
  'usage.admin.service.avg_latency': 'Avg latency',
  'usage.admin.service.rate_limited': 'Rate-limited',
  'usage.admin.service.recovery_events': 'Recovery events',
  'usage.admin.seat.title': 'Usage by seat',
  'usage.admin.seat.subtitle': '{count} members · sorted by total tokens',
  'usage.admin.seat.search_placeholder': 'Search name, email, org unit...',
  'usage.admin.seat.search_label': 'Search seats',
  'usage.admin.seat.empty': 'No member usage matches this window.',
  'usage.admin.seat.column.member': 'Member',
  'usage.admin.seat.column.team': 'Org unit',
  'usage.admin.seat.column.primary_model': 'Primary model',
  'usage.admin.seat.column.avg_per_request': 'Avg / req',
  'usage.admin.seat.column.monthly_cap': 'Monthly cap',
  'usage.admin.seat.column.trend_14d': '14d trend',
  'usage.admin.seat.pagination_aria': 'Seat pages',
  'usage.member.title': 'My token usage',
  'usage.member.subtitle': 'Your own usage, budget metadata, API keys, and request billing logs.',
  'usage.member.summary.scope_detail': '{range} personal scope',
  'usage.member.summary.cache_write_detail': '{range} cache creation',
  'usage.member.summary.cache_read_detail': '{range} cache reuse',
  'usage.member.summary.spend_detail': 'shown from billing metadata',
  'usage.member.summary.cache_hit_detail': '{rate} token hit · {count} ok',
  'usage.member.trend.title': 'Personal token trend',
  'usage.member.keys.title': 'My API keys',
  'usage.member.keys.empty': 'No key usage in this window.',
  'usage.member.keys.cache_rates': 'Req {request} · Token {token} · R/W {ratio}',
  'usage.member.requests.title': 'Recent requests',
  'usage.member.requests.empty': 'No request metadata in this window.',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': 'Request logs',
  'requests.subtitle': 'Inspect each request by status, model, tokens, cache, and cost.',
  'requests.range.aria_label': 'Request log time range',
  'requests.filter.caption': 'Filters',
  'requests.list.caption': 'Request log',
  'requests.filter.request_id': 'Request ID',
  'requests.filter.request_id_placeholder': 'Exact request id',
  'requests.filter.key': 'Key',
  'requests.filter.key_all': 'All of my keys',
  'requests.filter.key_all_org': 'All keys in the organization',
  'requests.filter.status': 'Status',
  'requests.filter.status_all': 'All statuses',
  'requests.filter.status_success': 'Success',
  'requests.filter.status_error': 'Error',
  'requests.filter.clear': 'Clear',
  'requests.expand': 'Expand details',
  'requests.collapse': 'Collapse details',
  'requests.empty.title': 'No requests in this window',
  'requests.empty.hint':
    'Integrate and send your first request — per-request billing metadata shows up here.',
  'requests.empty.docs': 'View integration docs',
  'requests.empty.filtered.title': 'No matching requests',
  'requests.empty.filtered.hint': 'Try loosening or clearing the filters.',
  'requests.pagination.aria': 'Request log pages',
  'requests.key.unnamed': 'Unnamed key',
  'requests.key.unknown': 'Unknown key',
  'requests.error': 'Could not load request logs.',
  'requests.column.time': 'Time',
  'requests.column.request': 'Request ID',
  'requests.column.key': 'Key name',
  'requests.column.model': 'Model',
  'requests.column.status': 'Status',
  'requests.column.duration': 'Duration',
  'requests.column.first_byte': 'First byte',
  'requests.column.input': 'Input',
  'requests.column.output': 'Output',
  'requests.column.cache_read': 'Cache hit',
  'requests.column.cache_write': 'Cache write',
  'requests.column.cost': 'Cost',
  'requests.cost.pending': 'Rating...',
  'requests.cost.not_charged.not_delivered': 'Not charged',
  'requests.cost.not_charged.pricing_review': 'Under review',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': 'Full request ID',
  'requests.detail.copy_request_id': 'Copy request ID',
  'requests.detail.filter_by_key': 'Filter by this key',
  'requests.detail.view_key_usage': 'View key usage',
  'requests.detail.api_key_id': 'Key ID',
  'requests.detail.protocol': 'Protocol',
  'requests.detail.billing': 'Billing',
  'requests.detail.billing.billed': 'Billed',
  'requests.detail.billing.not_billed': 'Not billed',
  'requests.detail.billing.pending': 'Rating in progress',
  'requests.detail.unit_billing': 'Unit billing',
  'requests.unit_billing.charged': '{quantity} {unit} · {amount}',
  'requests.unit_billing.unit.call': 'calls',
  'requests.unit_billing.unit.second': 'seconds',
  'requests.unit_billing.unit.character': 'characters',
  'requests.unit_billing.unit.image': 'images',
  'requests.unit_billing.not_charged.not_delivered': 'Not delivered — not charged',
  'requests.unit_billing.not_charged.pricing_review': 'Under billing review',
  'requests.detail.error_category': 'Category',
  'requests.status_category.success': 'Success',
  'requests.status_category.request_error': 'Request error',
  'requests.status_category.rate_limited': 'Rate limited',
  'requests.status_category.service_error': 'Service error',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': 'Model pricing',
  // #1706: with unit-metered models in the catalog, the subtitle no longer
  // claims the per-Mtok dimension for the whole page.
  'pricing.subtitle': 'Compare actual charges by provider, channel, and model.',
  'pricing.unit': 'per 1M tokens · CNY',
  // #1706: neutral badge for groups containing unit-metered rows; each unit
  // line names its own dimension (Per image / Per call / …).
  'pricing.unit.neutral': 'Prices · CNY',
  'pricing.loading': 'Loading model pricing…',
  'pricing.error': 'Could not load model pricing.',
  'pricing.empty': 'No model pricing is ready to display yet.',
  'pricing.no_results': 'No model prices match the current filters.',
  'pricing.vendor.summary': '{models} models · {channels} channels',
  'pricing.vendor.no_channels': 'This provider has no available channels right now.',
  'pricing.retry': 'Retry',
  'pricing.filter.all_vendors': 'All providers',
  'pricing.filter.all_channels': 'All channels',
  'pricing.filter.vendor_label': 'Filter by provider',
  'pricing.filter.channel_label': 'Filter by channel',
  'pricing.search.placeholder': 'Search models…',
  'pricing.column.model': 'Model',
  'pricing.cell.unavailable': 'Unavailable',
  'pricing.rate.input': 'Input',
  'pricing.rate.output': 'Output',
  'pricing.rate.cache_read': 'Cache hit',
  'pricing.rate.cache_write': 'Cache write',
  'pricing.rate.cache_write_5m': 'Cache write 5m',
  'pricing.rate.cache_write_1h': 'Cache write 1h',
  'pricing.rate.not_billed': 'Not billed',
  // #1706 — per-unit price labels for unit-metered SKUs; unit nouns are the
  // same closed set as requests.unit_billing.unit.*.
  'pricing.rate.unit.call': 'Per call',
  'pricing.rate.unit.second': 'Per second',
  'pricing.rate.unit.character': 'Per character',
  'pricing.rate.unit.image': 'Per image',
  'pricing.rate.unit.pending': 'Pricing pending',
  // Epic #898 P6 (#912) — namespace-grouped SKU market view.
  'pricing.filter.all_namespaces': 'All namespaces',
  'pricing.filter.namespace_label': 'Filter by namespace',
  'pricing.namespace.summary': '{skus} models',
  'pricing.column.rate': 'Rate',
  'pricing.column.prices': 'Prices',
  'pricing.sku.discount': 'Discount',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': 'Balance & billing',
  'billing.subtitle': 'Check your balance, top-ups, credits, and ledger history.',
  'billing.retry': 'Retry',
  'billing.balance.title': 'Current balance',
  'billing.balance.status.funded': 'Balance available',
  'billing.balance.status.zero': 'Needs top-up',
  'billing.balance.error': 'Could not load balance.',
  'billing.balance.updated': 'Updated {timestamp}',
  'billing.gift.included': 'Includes {amount} gift credit',
  'billing.gift.partial': 'of which {amount} {when}',
  'billing.gift.expires_in_days': 'expires in {days} days',
  'billing.gift.expires_in_day': 'expires in 1 day',
  'billing.gift.expires_today': 'expires today',
  'billing.gift.expired': 'expired, pending reclaim',
  'billing.credits.title': 'Balance breakdown',
  'billing.credits.subtitle':
    'Split by credit batch. Charges consume top to bottom: expiring gift credit first, then paid balance.',
  'billing.credits.error': 'Could not load the balance breakdown.',
  'billing.credits.empty': 'No credit batches available.',
  'billing.credits.column.kind': 'Source',
  'billing.credits.column.remaining': 'Remaining',
  'billing.credits.column.amount': 'Credited',
  'billing.credits.column.expires': 'Expires',
  'billing.credits.column.issued': 'Credited at',
  'billing.credits.kind.promo': 'Gift',
  'billing.credits.kind.cash': 'Paid',
  'billing.credits.never_expires': 'Never expires',
  'billing.forbidden.title': 'Billing is managed by your org admin',
  'billing.forbidden.body':
    'Members can manage their own API keys and usage, but enterprise balance, top-ups, credits, and ledger history require admin billing access.',
  'billing.forbidden.cta': 'Back to dashboard',
  'billing.topup.entry': 'Top up',
  'billing.redeem.title': 'Redeem gift card',
  'billing.redeem.code_label': 'Gift-card code',
  'billing.redeem.code_placeholder': '32-character hex code',
  'billing.redeem.submit': 'Redeem',
  'billing.redeem.error.code_required': 'Gift-card code is required',
  'billing.redeem.toast.success': 'Credited {amount}, balance {balance}',
  'billing.transactions.title': 'Balance history',
  'billing.transactions.latest': 'Latest',
  'billing.transactions.page': 'Page {page}',
  'billing.transactions.error': 'Could not load transactions.',
  'billing.transactions.empty': 'No transactions yet.',
  'billing.transactions.column.date': 'Date',
  'billing.transactions.column.type': 'Type',
  'billing.transactions.column.amount_usd': 'Amount (CNY)',
  'billing.transactions.column.running': 'Balance',
  'billing.transactions.newer': 'Newer',
  'billing.transactions.older': 'Older',
  'billing.transactions.type.charge': 'Request charges',
  'billing.transactions.type.gift_card_redeem': 'Gift card credit',
  'billing.transactions.type.gift_direct_issue': 'Gift credit',
  'billing.transactions.type.gift_expiry_reclaim': 'Expired gift reclaimed',
  'billing.transactions.type.adjustment': 'Manual adjustment',
  'billing.transactions.type.topup': 'Top-up credit',
  'billing.transactions.type.external_topup': 'Offline top-up credit',

  // ── Billing statements (credit customers, #1133) ───────────────
  'billing.statements.title': 'Statements',
  'billing.statements.subtitle':
    'Accounts receivable per billing period. Consumption is informational — the amount owed is the closing AR.',
  'billing.statements.error': 'Could not load statements.',
  'billing.statements.column.period': 'Period',
  'billing.statements.column.consumption': 'Usage (info)',
  'billing.statements.column.closing_ar': 'Closing AR',
  'billing.statements.column.due': 'Due date',
  'billing.statements.column.status': 'Status',
  'billing.statements.status.settled': 'Settled',
  'billing.statements.status.open': 'Open',
  'billing.statements.status.overdue': 'Overdue',
  'billing.statements.detail.title': 'Statement detail',
  'billing.statements.detail.period': 'Period',
  'billing.statements.detail.consumption': 'Usage (info)',
  'billing.statements.detail.opening_ar': 'Opening AR',
  'billing.statements.detail.payments': 'Payments applied',
  'billing.statements.detail.closing_ar': 'Closing AR',
  'billing.statements.detail.cny_reference': 'CNY reference',
  'billing.statements.detail.due': 'Due date',
  'billing.statements.detail.close': 'Close',
  'billing.statements.detail.consumption_hint':
    'Usage is the gross consumption for the period, for reference only; the amount owed is the closing AR.',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': 'Top up',
  'topup.subtitle':
    'Fund your balance with WeChat Pay. The next step shows a QR code you can scan with your phone.',
  'topup.forbidden.title': 'Top-up restricted',
  'topup.forbidden.body': 'Only org owners and admins can add balance.',
  'topup.forbidden.back': 'Back to billing',
  'topup.form.caption': 'Top-up order',
  'topup.amount.label': 'Amount (CNY · whole yuan)',
  'topup.amount.hint': 'CNY · min {min} · max {max} · whole yuan only',
  'topup.usd.label': 'Balance value you will receive',
  'topup.usd.hint':
    'Converted at about {rate} ¥/$. The internal USD credit is locked when the order is created.',
  'topup.usd.hint_loading': 'Loading the current server quote.',
  'topup.usd.hint_unavailable':
    'Quote unavailable. The internal credit will be locked by the server when the order is created.',
  'topup.pay_type.legend': 'Payment method',
  'topup.pay_type.wxpay': 'WeChat Pay',
  'topup.error.whole_yuan': 'Enter a whole-yuan amount (integer, no decimals).',
  'topup.error.min': 'Minimum top-up is {amount}.',
  'topup.error.max': 'Maximum top-up is {amount}.',
  'topup.error.no_redirect': 'Payment channel did not return a redirect URL.',
  'topup.cancel': 'Cancel',
  'topup.submit': 'Continue to pay →',
  'topup.fee_hint':
    'Exchange rates refresh regularly, and Nadoutong covers the payment-channel fee.',
  'topup.qr.title': 'Scan to pay',
  'topup.qr.order_prefix': 'Order ',
  'topup.qr.image_alt': 'Payment QR code',
  'topup.qr.fallback_hint': 'Open WeChat on your phone and scan the target URL below.',
  'topup.qr.status.paid': 'Payment received. Redirecting…',
  'topup.qr.status.other': 'Order {status}',
  'topup.qr.status.pending': 'Waiting for payment…',
  'topup.qr.status.slow': 'Taking longer than expected…',
  'topup.qr.slow_notice':
    "We've stopped polling to save bandwidth. If you already paid in the app, your balance will update on its own — it's safe to close this.",
  'topup.qr.close': 'Close',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.subtitle': 'Manage account, security, and technical information.',
  'settings.account.title': 'Account details',
  'settings.account.description': 'This section shows the information you use most often.',
  'settings.account.email': 'Email',
  'settings.account.account_id': 'Account id',
  'settings.account.org_id': 'Team id',
  'settings.account.email_verified': 'Email verified',
  'settings.account.status.verified': 'verified',
  'settings.account.status.unverified': 'unverified',
  'settings.technical.summary':
    'Raw IDs and other technical fields live here for troubleshooting when you need them.',
  // Remaining consumers: src/pages/Invite.tsx role labels (the rest of
  // the settings.invite_modal.* family retired with the PR 9 fork).
  'settings.invite_modal.role.member': 'Member',
  'settings.invite_modal.role.admin': 'Admin',
  'settings.security.title': 'Security',
  'settings.security.change_password': 'Change sign-in password',
  'settings.security.current_password': 'Current password',
  'settings.security.new_password': 'New password (10–128 chars)',
  'settings.security.confirm_password': 'Confirm new password',
  'settings.security.submit': 'Change password',
  'settings.security.error.current_required': 'Current password is required',
  'settings.security.error.current_incorrect': 'Current password is incorrect',
  'settings.security.error.password_too_short': 'Password must be at least {min} characters',
  'settings.security.error.password_too_long': 'Password must be at most {max} characters',
  'settings.security.error.mismatch': 'Passwords do not match',
  'settings.security.error.network': 'Network error — try again',
  'settings.security.toast.success': 'Password changed',

  // ── Team pages (/team/*) ────────────────────────────────────────
  'team.retry': 'Retry',
  'team.forbidden': 'Only owners and admins can view team management.',
  'team.role.owner': 'Owner',
  'team.role.admin': 'Admin',
  'team.role.member': 'Member',

  // Members page
  'team.members.title': 'Members',
  'team.members.subtitle': 'Assign members to departments; usage views use this assignment first.',
  'team.members.invite': 'Invite member',
  'team.members.summary.members': 'Members',
  'team.members.summary.members_hint': 'Owners / admins {count}',
  'team.members.summary.units': 'Departments',
  'team.members.summary.units_hint': 'Assigned members {count}',
  'team.members.summary.unassigned': 'Unassigned members',
  'team.members.summary.unassigned_hint': 'Limited-access members {count}',
  'team.members.summary.pending': 'Pending invites',
  'team.members.directory.title': 'Member directory',
  'team.members.filter.search_placeholder': 'Search name, email, department',
  'team.members.filter.search_label': 'Search members',
  'team.members.filter.unit_label': 'Filter by department',
  'team.members.filter.all_units': 'All departments',
  'team.members.filter.unassigned': 'Unassigned',
  'team.members.empty': 'No members yet.',
  'team.members.no_match': 'No matching members.',
  'team.members.error.load': 'Could not load members',
  'team.members.error.units_load': 'Could not load departments',
  'team.members.column.member': 'Member',
  'team.members.column.unit': 'Department',
  'team.members.column.role': 'Role',
  'team.members.column.permissions': 'Model permissions',
  'team.members.column.keys': 'Keys',
  'team.members.column.joined': 'Joined',
  'team.members.permission.limited': 'Limited',
  'team.members.permission.unrestricted': 'Unrestricted',
  'team.members.unit.aria': "Change {member}'s department",
  'team.members.toast.unit_updated': 'Department updated',
  'team.members.error.unit_update': 'Could not update department',

  // Member API keys (count button + dialog)
  'team.members.keys.loading': 'Loading',
  'team.members.keys.error': 'Could not load member API keys',
  'team.members.keys.owner_note': 'Managed on the API Keys page',
  'team.members.keys.manage': "View {member}'s API keys",
  'team.members.keys.dialog_title': "{member}'s API keys",
  'team.members.keys.dialog_subtitle':
    'Revoke keys assigned to this member account from the member directory. Owner keys stay on the API Keys page.',
  'team.members.keys.close': 'Close',
  'team.members.keys.empty': 'This member has no active API keys.',
  'team.members.keys.column.name': 'Name',
  'team.members.keys.column.prefix': 'Prefix',
  'team.members.keys.column.created': 'Created',
  'team.members.keys.column.last_used': 'Last used',
  'team.members.keys.column.actions': 'Actions',
  'team.members.keys.never_used': 'Never used',
  'team.members.keys.revoke': 'Revoke',
  'team.members.keys.revoke_title': 'Revoke API key',
  'team.members.keys.revoke_body':
    'Revoking this key immediately disables it. Clients must switch to a replacement key.',
  'team.members.keys.revoke_confirm': 'Confirm revoke',
  'team.members.keys.revoke_cancel': 'Cancel',
  'team.members.keys.toast.revoked': 'API key revoked',
  'team.members.keys.revoke_error': 'Could not revoke API key',

  // Pending invitations (single home on /team/members)
  'team.invitations.title': 'Pending invitations',
  'team.invitations.empty':
    'No pending invitations. Valid invites that have not been accepted or revoked appear here.',
  'team.invitations.error.load': 'Could not load invitations',
  'team.invitations.column.email': 'Email',
  'team.invitations.column.role': 'Invite role',
  'team.invitations.column.expires': 'Expires',
  'team.invitations.column.actions': 'Actions',
  'team.invitations.revoke': 'Revoke',
  'team.invitations.revoke_title': 'Revoke invitation',
  'team.invitations.revoke_body':
    'After revoking, {email} can no longer join through this invite link.',
  'team.invitations.revoke_confirm': 'Confirm revoke',
  'team.invitations.revoke_cancel': 'Cancel',
  'team.invitations.close': 'Close',
  'team.invitations.toast.revoked': 'Invitation revoked',
  'team.invitations.revoke_error': 'Could not revoke invitation',

  // Invite drawer (local rebuild of the shared invite modal flow)
  'team.invite.title': 'Invite member',
  'team.invite.description': 'Invite a new member to the team by email.',
  'team.invite.close': 'Close',
  'team.invite.email_label': 'Email',
  'team.invite.email_placeholder': 'name@example.com',
  'team.invite.email_required': 'Enter an email address',
  'team.invite.role_label': 'Role',
  'team.invite.role.member': 'Member',
  'team.invite.role.admin': 'Admin',
  'team.invite.submit': 'Send invitation',
  'team.invite.cancel': 'Cancel',
  'team.invite.toast.created': 'Invitation created',
  'team.invite.link_title': 'Invite link',
  'team.invite.link_hint':
    'Share the link with the invitee; it can be used once before it expires.',
  'team.invite.copy': 'Copy link',
  'team.invite.copied': 'Copied',
  'team.invite.done': 'Done',

  // Model permissions page
  'team.permissions.title': 'Model permissions',
  'team.permissions.subtitle': 'Configure allowed and default models per member or department.',
  'team.permissions.members.title': 'Members',
  'team.permissions.units.title': 'Departments',
  'team.permissions.members.empty': 'No members yet.',
  'team.permissions.units.empty': 'No departments yet.',
  'team.permissions.error.members_load': 'Could not load members',
  'team.permissions.error.units_load': 'Could not load departments',
  'team.permissions.configure': 'Configure',
  'team.permissions.mode.inherit': 'Inherit',
  'team.permissions.mode.all': 'Allow all',
  'team.permissions.mode.custom': 'Custom',
  'team.permissions.mode_help.inherit': 'Follow the parent policy.',
  'team.permissions.mode_help.all': 'Allow current and future catalog models.',
  'team.permissions.mode_help.custom': 'Use only the selected models below.',

  // Model permissions drawer (member | unit targets)
  'team.permissions.drawer.member_title': 'Member model permissions',
  'team.permissions.drawer.unit_title': 'Department model permissions',
  'team.permissions.drawer.member_subtitle': 'Configure the models available to {target}.',
  'team.permissions.drawer.unit_subtitle': 'Configure the models inherited by {target}.',
  'team.permissions.drawer.close': 'Close',
  'team.permissions.drawer.mode_label': 'Mode',
  'team.permissions.drawer.selected_count': '{count} selected',
  'team.permissions.drawer.default_model': 'Default {model}',
  'team.permissions.drawer.set_default': 'Set default: {model}',
  'team.permissions.drawer.search_label': 'Search models',
  'team.permissions.drawer.search_placeholder': 'Search models',
  'team.permissions.drawer.empty': 'No configurable models.',
  'team.permissions.drawer.no_search': 'No matching models.',
  'team.permissions.drawer.select_group': 'Select all',
  'team.permissions.drawer.clear_group': 'Clear',
  'team.permissions.drawer.group_count': '{selected}/{total} models',
  'team.permissions.drawer.legacy_title': 'Legacy grants',
  'team.permissions.drawer.legacy_body':
    'These grants no longer match a catalog listing; saving keeps them unchanged.',
  'team.permissions.drawer.global_source': 'Global catalog',
  'team.permissions.drawer.inherited_from': 'From {source}',
  'team.permissions.drawer.inherit_active': 'Inheriting from {source}',
  'team.permissions.drawer.inherit_body': 'Switch to Custom to create an override for this node.',
  'team.permissions.drawer.all_active': 'All models are allowed',
  'team.permissions.drawer.all_body': 'New catalog models are included automatically.',
  'team.permissions.drawer.loading': 'Loading model permissions…',
  'team.permissions.drawer.load_error': 'Could not load model permissions',
  'team.permissions.drawer.save': 'Save',
  'team.permissions.drawer.cancel': 'Cancel',
  'team.permissions.toast.member_saved': 'Member model permissions saved',
  'team.permissions.toast.unit_saved': 'Department model permissions saved',

  // Org units page (departments)
  'team.units.title': 'Departments',
  'team.units.subtitle': 'Review departments by path and direct member count.',
  'team.units.create': 'New department',
  'team.units.empty.title': 'No departments yet',
  'team.units.empty.hint': 'Create a department to assign members to the right team.',
  'team.units.error.load': 'Could not load departments',
  'team.units.column.name': 'Name',
  'team.units.column.parent': 'Parent',
  'team.units.column.members': 'Direct members',
  'team.units.column.status': 'Status',
  'team.units.column.actions': 'Actions',
  'team.units.status.active': 'Active',
  'team.units.status.archived': 'Archived',
  'team.units.root_parent': 'Top-level department',
  'team.units.edit': 'Edit',
  'team.units.archive': 'Archive',
  'team.units.drawer.create_title': 'New department',
  'team.units.drawer.edit_title': 'Edit department',
  'team.units.drawer.description': 'Departments group members for assignment and usage rollups.',
  'team.units.drawer.close': 'Close',
  'team.units.form.name_label': 'Name',
  'team.units.form.name_placeholder': 'Engineering',
  'team.units.form.name_required': 'Enter a department name',
  'team.units.form.parent_label': 'Parent',
  'team.units.form.submit_create': 'Create',
  'team.units.form.submit_save': 'Save',
  'team.units.form.cancel': 'Cancel',
  'team.units.toast.created': 'Department created',
  'team.units.toast.saved': 'Department updated',
  'team.units.error.save': 'Could not save department',
  'team.units.archive_title': 'Archive department',
  'team.units.archive_body':
    'After archiving {unit}, it can no longer be used for member assignment.',
  'team.units.archive_confirm': 'Confirm archive',
  'team.units.archive_cancel': 'Cancel',
  'team.units.toast.archived': 'Department archived',
  'team.units.archive_error': 'Could not archive department',

  // Audit page
  'team.audit.title': 'Audit',
  'team.audit.subtitle': 'Trace member, invite, model-permission, key, and org-structure changes.',
  'team.audit.forbidden': 'This account does not have audit log access.',
  'team.audit.filter.caption': 'Filters',
  'team.audit.filter.action_label': 'Action',
  'team.audit.filter.action_placeholder': 'Filter by action, e.g. member_model_permissions.updated',
  'team.audit.filter.target_label': 'Target account',
  'team.audit.filter.target_placeholder': 'Filter by target account id',
  'team.audit.filter.clear': 'Clear',
  'team.audit.events.title': 'Audit log',
  'team.audit.error.load': 'Could not load audit events',
  'team.audit.empty':
    'No audit events yet. Member, invite, key, model-permission, and org-structure changes appear here.',
  'team.audit.column.time': 'Time',
  'team.audit.column.actor': 'Actor',
  'team.audit.column.action': 'Action',
  'team.audit.column.target': 'Target account',
  'team.audit.column.detail': 'Metadata',
  'team.audit.actor_system': 'system',
  'team.audit.pagination.previous': 'Previous',
  'team.audit.pagination.next': 'Next',
  'team.audit.pagination.aria': 'Audit log pagination',

  // ── i18n switcher chrome ───────────────────────────────────────
  'i18n.switcher.aria_label': 'Language',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': 'Notifications',
  'toast.dismiss_aria': 'Dismiss notification',

  // ── Billing topup return ────────────────────────────────────────
  'billing.topup_return.page.title': 'Topup result',
  'billing.topup_return.page.order_label': 'Order ',
  'billing.topup_return.page.waiting_order_id': 'Waiting for order id…',
  'billing.topup_return.loading.aria': 'Loading order status…',
  'billing.topup_return.missing.title': 'Missing order id',
  'billing.topup_return.missing.body':
    "This page is normally reached from the payment provider's return link. If you landed here by mistake, go back to the billing overview.",
  'billing.topup_return.missing.cta': 'Return to billing',
  'billing.topup_return.pending.title': 'Payment in progress',
  'billing.topup_return.pending.body':
    'Payment is being processed. This page will update automatically once the provider confirms — no need to reload.',
  'billing.topup_return.slow.title': 'Payment takes longer than usual',
  'billing.topup_return.slow.body':
    "The provider hasn't confirmed your payment yet. If you already completed payment in the app, the balance will update within a few minutes — you can safely close this tab.",
  'billing.topup_return.slow.reference': 'Reference order id:',
  'billing.topup_return.slow.cta.back': 'Back to billing',
  'billing.topup_return.slow.cta.support': 'Contact support',
  'billing.topup_return.paid.title': 'Payment received',
  'billing.topup_return.paid.credited': 'Credited',
  'billing.topup_return.paid.charged': 'Charged',
  'billing.topup_return.paid.at_rate': 'at {rate} ¥/$',
  'billing.topup_return.paid.new_balance': 'New balance',
  'billing.topup_return.paid.balance_loading': 'Loading…',
  'billing.topup_return.paid.technical': 'Technical ledger details',
  'billing.topup_return.paid.usd_credit': 'USD credit',
  'billing.topup_return.paid.usd_ledger': 'USD ledger balance',
  'billing.topup_return.paid.redirect_hint':
    'Redirecting to the billing overview in a few seconds…',
  'billing.topup_return.paid.cta': 'Return to dashboard',
  'billing.topup_return.terminal.expired.title': 'Order expired',
  'billing.topup_return.terminal.expired.body':
    'This order expired before payment was completed. Start a new topup if you still want to add balance.',
  'billing.topup_return.terminal.failed.title': 'Payment failed',
  'billing.topup_return.terminal.failed.body':
    'The payment provider reported a failure for this order. No funds were charged — you can safely try again.',
  'billing.topup_return.terminal.refunded.title': 'Order refunded',
  'billing.topup_return.terminal.refunded.body':
    'This order was refunded. Start a new topup if you want to re-credit your balance.',
  'billing.topup_return.terminal.cta.retry': 'Try again',
  'billing.topup_return.terminal.cta.back': 'Back to billing',

  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': 'Loading…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': 'Unexpected error',
};
